/**
 * Native push registration (Capacitor: FCM on Android, APNs on iOS).
 *
 * Migration plan P0-20 / Phase 1 "push registered at start". Until now a device
 * registered for push only when someone opened Account Settings and switched
 * the toggle on, so most field phones never received an SOS or evacuation
 * push. Now the device registers as soon as there is a signed-in session: at
 * app start (including when the persisted session rehydrates), after sign-in,
 * and when the signed-in user changes.
 *
 * Rules:
 * - Native platforms only. Web push stays opt-in from Account Settings.
 * - The OS permission prompt is shown at most once per app launch, and never
 *   once the OS reports `denied`.
 * - Switching push off for this device in Account Settings is remembered per
 *   user on this device, and startup registration respects it.
 * - The plugin listeners live for the whole app session. They are never removed
 *   wholesale: `removeAllListeners()` would also drop other features'
 *   listeners (zone sync in useZoneSyncListener, for one).
 * - A tapped notification only ever opens an in-app route (see
 *   pushTargetToInAppPath). Its URL is server-controlled (an automation's
 *   action_url is a template a tenant user writes), so anything else is
 *   ignored. Nothing here assigns `location.href`: a `javascript:` URL there
 *   would run in the app's origin, next to the Capacitor plugins and the
 *   stored session.
 *
 * Who the device token belongs to:
 * - An explicit sign-out (`logout('user')`) deletes the device token at once,
 *   so a shared device stops receiving the previous user's alerts. The plugin
 *   call is made directly from the auth-store subscription, not queued behind a
 *   registration that may still be waiting for a token, because the sign-out
 *   paths reload the page straight away. Capacitor hands the call to the native
 *   side in a microtask, which runs before the page can unload. A "drop
 *   pending" flag is written first and cleared only when the plugin confirms,
 *   so the next start retries the deletion if the page went away first.
 * - An automatic sign-out (idle timeout, expired session) keeps the
 *   registration. It is the same person's phone, and SOS and evacuation pushes
 *   must keep reaching it until they sign in again.
 * - The user the token was last posted for is remembered on the device. When a
 *   different user signs in, or a deletion is still pending, the old token is
 *   deleted before the new user registers. That holds across reloads, where
 *   the in-memory session is gone. The backend also gives one device token to
 *   one user only (push.service subscribeNative).
 *
 * `start()` is called once from main.tsx and follows the auth store with a
 * synchronous subscription.
 */
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '@/services/api';
import { useAuthStore, type LogoutReason } from '@/store/auth.store';
import { toast } from '@/utils/toast';
import { parseApiError, getErrorMessage } from '@/utils/api-errors';
import { safeInternalPath } from '@/utils/safe-url';

export type NativePushPermission = 'granted' | 'denied' | 'default';

export interface NativePushState {
  permission: NativePushPermission;
  /** This device's token is registered on the server for the signed-in user. */
  registered: boolean;
  error: string | null;
}

/** How long to wait for FCM/APNs to hand back a token before giving up. */
export const NATIVE_PUSH_REGISTRATION_TIMEOUT_MS = 20_000;

const OPT_OUT_KEY_PREFIX = 'cmdbridge.nativePush.optOut.';
/** The user this device's push token was last posted to the server for. */
export const NATIVE_PUSH_DEVICE_USER_KEY = 'cmdbridge.nativePush.deviceUser';
/** '1' while an explicit sign-out's token deletion is not yet confirmed. */
export const NATIVE_PUSH_DROP_PENDING_KEY = 'cmdbridge.nativePush.dropPending';

function nativePlatform(): 'android' | 'ios' {
  return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
}

function toPermission(receive: string | undefined): NativePushPermission {
  if (receive === 'granted') return 'granted';
  if (receive === 'denied') return 'denied';
  return 'default'; // 'prompt' and 'prompt-with-rationale'
}

function readKey(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the value lasts for this session only.
  }
}

function isOptedOut(userId: string): boolean {
  return readKey(OPT_OUT_KEY_PREFIX + userId) === '1';
}

function setOptedOut(userId: string, optedOut: boolean): void {
  writeKey(OPT_OUT_KEY_PREFIX + userId, optedOut ? '1' : null);
}

function signedInUserId(state: { isAuthenticated: boolean; user: { id: string } | null }): string | null {
  return state.isAuthenticated && state.user?.id ? state.user.id : null;
}

/**
 * The in-app route a tapped notification should open, or null to ignore it.
 *
 * Same rule as in-app notifications (safeInternalPath): only an internal route
 * is accepted. A same-origin absolute URL is reduced to its path first.
 * Everything else is rejected: `javascript:` and other schemes, other origins,
 * protocol-relative `//host`, and `/\host` (which browsers resolve to another
 * host).
 */
export function pushTargetToInAppPath(target: unknown): string | null {
  if (typeof target !== 'string') return null;
  let candidate = target.trim();
  if (/^https?:\/\//i.test(candidate)) {
    let absolute: URL;
    try {
      absolute = new URL(candidate);
    } catch {
      return null;
    }
    if (absolute.origin !== window.location.origin) return null;
    candidate = absolute.pathname + absolute.search + absolute.hash;
  }
  const internal = safeInternalPath(candidate);
  if (!internal) return null;
  try {
    const resolved = new URL(internal, window.location.origin);
    if (resolved.origin !== window.location.origin) return null;
    return resolved.pathname + resolved.search + resolved.hash;
  } catch {
    return null;
  }
}

/**
 * Open a tapped notification's target inside the app: history.pushState plus a
 * popstate event, which React Router follows without a reload. A target that
 * is not an in-app route is ignored (the app simply opens where it was).
 * Returns whether it navigated.
 */
export function navigateToPushTarget(target: unknown): boolean {
  const path = pushTargetToInAppPath(target);
  if (path === null) {
    console.warn('[NativePush] ignored a notification target that is not an in-app route');
    return false;
  }
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  return true;
}

export class NativePushService {
  private started = false;
  private listenersReady: Promise<void> | null = null;
  private readonly handles: PluginListenerHandle[] = [];
  /** The signed-in user this device registers for (null when signed out). */
  private userId: string | null = null;
  /** The last token FCM/APNs handed back. */
  private token: string | null = null;
  /** The user `token` was last posted to the server for. */
  private registeredFor: string | null = null;
  private permission: NativePushPermission = 'default';
  private lastError: string | null = null;
  private promptedThisLaunch = false;
  private waiters: Array<(ok: boolean) => void> = [];
  /** Serialises registrations so a sign-in never races another. */
  private queue: Promise<unknown> = Promise.resolve();
  /** An explicit sign-out's token deletion, while it is running. */
  private dropInFlight: Promise<boolean> | null = null;
  private readonly changeListeners = new Set<() => void>();
  private unsubscribeAuth: (() => void) | null = null;

  /** Begin following the signed-in session. Idempotent; a no-op on the web. */
  start(): void {
    if (this.started || !Capacitor.isNativePlatform()) return;
    this.started = true;
    void this.ensureListeners();

    const current = signedInUserId(useAuthStore.getState());
    if (current === null && readKey(NATIVE_PUSH_DROP_PENDING_KEY) === '1') {
      // Last session's explicit sign-out may not have reached the plugin before
      // the page reloaded. (With a signed-in user, register() handles it.)
      void this.enqueue(() => this.deleteDeviceToken());
    }
    this.onSessionChange(current, null, null);
    this.unsubscribeAuth = useAuthStore.subscribe((state, prev) => {
      const next = signedInUserId(state);
      const before = signedInUserId(prev);
      if (next !== before) this.onSessionChange(next, before, state.lastLogoutReason);
    });
  }

  /**
   * Stop following the session and detach this service's own listeners (one
   * by one; never removeAllListeners). The app never calls this; tests do.
   */
  stop(): void {
    this.unsubscribeAuth?.();
    this.unsubscribeAuth = null;
    this.handles.splice(0).forEach((handle) => {
      void handle.remove();
    });
    this.listenersReady = null;
    this.started = false;
  }

  getState(): NativePushState {
    return {
      permission: this.permission,
      registered: this.userId !== null && this.registeredFor === this.userId,
      error: this.lastError,
    };
  }

  /** Re-read the OS permission (for the Account Settings toggle). */
  async refreshPermission(): Promise<NativePushPermission> {
    this.permission = toPermission((await PushNotifications.checkPermissions()).receive);
    this.emit();
    return this.permission;
  }

  /** Account Settings "on": clears this user's opt-out and registers now. */
  enable(): Promise<boolean> {
    const userId = this.userId;
    if (!userId) return Promise.resolve(false);
    setOptedOut(userId, false);
    return this.enqueue(() => this.register(userId, { explicit: true }));
  }

  /** Account Settings "off": remembers the opt-out and removes the server registration. */
  disable(): Promise<boolean> {
    const userId = this.userId;
    if (!userId) return Promise.resolve(false);
    setOptedOut(userId, true);
    return this.enqueue(async () => {
      try {
        if (this.token && this.registeredFor === userId) {
          await api.post('/push/unsubscribe', { token: this.token, platform: nativePlatform() });
        }
      } catch (err) {
        // Still registered server-side, so the toggle must stay on.
        setOptedOut(userId, false);
        this.lastError = getErrorMessage(parseApiError(err)) || 'Failed to unsubscribe';
        this.emit();
        throw err;
      }
      this.registeredFor = null;
      this.lastError = null;
      this.emit();
      return true;
    });
  }

  /** Subscribe to state changes. Returns an unsubscribe function. */
  onChange(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }

  // ---- internals ------------------------------------------------------------

  private onSessionChange(
    next: string | null,
    before: string | null,
    reason: LogoutReason | null | undefined
  ): void {
    this.userId = next;
    if (before !== null && (next !== null || reason === 'user' || reason === 'revoked')) {
      // An explicit sign-out, a revoked credential (terminal auth code), or a
      // different user replacing the session in place: the token is the
      // previous user's. An automatic sign-out (idle timeout, expired session)
      // keeps it, so the same person stays reachable.
      this.dropDeviceTokenNow();
    }
    if (next !== null) {
      void this.enqueue(() => this.register(next, { explicit: false }));
    }
    this.emit();
  }

  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run = this.queue.then(task, task);
    this.queue = run.catch(() => undefined);
    return run;
  }

  /**
   * Delete the device token now rather than behind queued work: a registration
   * can wait up to NATIVE_PUSH_REGISTRATION_TIMEOUT_MS for a token, and every
   * explicit sign-out reloads the page at once. The pending flag goes first,
   * so the next start retries if the page is gone before the plugin answers.
   */
  private dropDeviceTokenNow(): void {
    writeKey(NATIVE_PUSH_DROP_PENDING_KEY, '1');
    // Release a registration waiting for a token so the queue cannot stall.
    this.settle(false);
    const drop = this.deleteDeviceToken();
    this.dropInFlight = drop;
    void drop.finally(() => {
      if (this.dropInFlight === drop) this.dropInFlight = null;
    });
  }

  /**
   * Delete this device's push token. The pending flag and the remembered owner
   * are cleared only once the plugin confirms. Never rejects.
   *
   * The plugin call comes before the first `await`, so a synchronous caller
   * (dropDeviceTokenNow) has made it by the time it returns.
   */
  private async deleteDeviceToken(): Promise<boolean> {
    this.registeredFor = null;
    this.token = null;
    try {
      await PushNotifications.unregister();
      writeKey(NATIVE_PUSH_DROP_PENDING_KEY, null);
      writeKey(NATIVE_PUSH_DEVICE_USER_KEY, null);
      return true;
    } catch (err) {
      // On a shared device this matters: the previous user's alerts may still
      // arrive until the backend reassigns or prunes the token.
      console.warn('[NativePush] could not delete the push token:', err);
      toast.warning(
        'Push notifications not fully reset',
        "This device may still receive the previous user's alerts until someone signs in on it again."
      );
      return false;
    } finally {
      this.emit();
    }
  }

  /** True when the device token may still belong to someone other than `userId`. */
  private tokenMayBelongToSomeoneElse(userId: string): boolean {
    if (readKey(NATIVE_PUSH_DROP_PENDING_KEY) === '1') return true;
    const owner = readKey(NATIVE_PUSH_DEVICE_USER_KEY);
    return owner !== null && owner !== userId;
  }

  private ensureListeners(): Promise<void> {
    if (!this.listenersReady) {
      this.listenersReady = (async () => {
        // Token from FCM/APNs: on register(), and again whenever it rotates.
        this.handles.push(
          await PushNotifications.addListener('registration', (token) => {
            void this.onToken(token.value);
          })
        );
        this.handles.push(
          await PushNotifications.addListener('registrationError', (error) => {
            this.onRegistrationError(error.error || 'Native push registration failed');
          })
        );
        // A push that arrives while the app is in the foreground. Data-only
        // pushes (zone sync and other silent syncs) have no title or body and
        // are handled by their own listeners, so they must not toast.
        this.handles.push(
          await PushNotifications.addListener('pushNotificationReceived', (notification) => {
            if (!notification.title && !notification.body) return;
            toast.info(notification.title || 'New notification', notification.body);
          })
        );
        // The user tapped a notification (the plugin retains a cold-start tap
        // until this listener exists). In-app routes only; see the header.
        this.handles.push(
          await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            const url: unknown = action.notification.data?.url;
            if (typeof url === 'string' && url) navigateToPushTarget(url);
          })
        );
      })().catch((err) => {
        console.warn('[NativePush] could not attach push listeners:', err);
        this.listenersReady = null;
      });
    }
    return this.listenersReady;
  }

  private async register(userId: string, opts: { explicit: boolean }): Promise<boolean> {
    if (this.userId !== userId) return false; // the session changed while queued

    try {
      // Never register a token that may still be someone else's: finish an
      // explicit sign-out's deletion, and delete the token first when it was
      // last posted for a different user or a deletion is still pending (this
      // survives reloads, unlike the in-memory session). A failed deletion does
      // not block this user: the backend moves the token to them on subscribe.
      if (this.dropInFlight) await this.dropInFlight;
      if (this.tokenMayBelongToSomeoneElse(userId)) await this.deleteDeviceToken();
      if (this.userId !== userId) return false;
      if (!opts.explicit && isOptedOut(userId)) return false;

      await this.ensureListeners();
      let permission = toPermission((await PushNotifications.checkPermissions()).receive);
      if (permission === 'default' && (opts.explicit || !this.promptedThisLaunch)) {
        this.promptedThisLaunch = true;
        permission = toPermission((await PushNotifications.requestPermissions()).receive);
      }
      this.permission = permission;
      if (permission !== 'granted') {
        if (opts.explicit) this.lastError = 'Notification permission denied';
        this.emit();
        return false;
      }
      if (this.userId !== userId) return false;
      return await this.awaitToken();
    } catch (err) {
      this.lastError = err instanceof Error ? err.message : 'Native push registration failed';
      this.emit();
      return false;
    }
  }

  /** Ask FCM/APNs for a token; resolves once onToken has posted it (or failed). */
  private awaitToken(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const finish = (ok: boolean) => {
        clearTimeout(timer);
        this.waiters = this.waiters.filter((w) => w !== finish);
        resolve(ok);
      };
      const timer = setTimeout(() => {
        this.lastError = 'Timed out waiting for a push token from the device';
        this.emit();
        finish(false);
      }, NATIVE_PUSH_REGISTRATION_TIMEOUT_MS);
      this.waiters.push(finish);
      PushNotifications.register().catch((err: unknown) => {
        this.onRegistrationError(err instanceof Error ? err.message : 'Native push registration failed');
      });
    });
  }

  private settle(ok: boolean): void {
    const waiters = this.waiters;
    this.waiters = [];
    waiters.forEach((w) => w(ok));
  }

  private async onToken(token: string): Promise<void> {
    this.token = token;
    const userId = this.userId;
    if (!userId || isOptedOut(userId)) {
      this.settle(false);
      return;
    }
    // Remember the owner before posting: if the POST lands but its reply is
    // lost, the server still holds this token for this user.
    writeKey(NATIVE_PUSH_DEVICE_USER_KEY, userId);
    try {
      await api.post('/push/subscribe', { token, platform: nativePlatform() });
      const stillCurrent = this.userId === userId;
      if (stillCurrent) {
        this.registeredFor = userId;
        this.lastError = null;
      }
      this.settle(stillCurrent);
    } catch (err) {
      this.lastError =
        getErrorMessage(parseApiError(err)) || 'Failed to register this device for push notifications';
      this.settle(false);
    }
    this.emit();
  }

  private onRegistrationError(message: string): void {
    this.lastError = message;
    this.settle(false);
    this.emit();
  }

  private emit(): void {
    this.changeListeners.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.warn('[NativePush] change listener failed:', err);
      }
    });
  }
}

export const nativePushService = new NativePushService();
